import { isPlatform } from "@ionic/react";

import {
  AuthMode,
  BiometricType,
  DefaultSession,
  IdentityVault,
  IonicIdentityVaultUser,
  IonicNativeAuthPlugin,
  PluginConfiguration,
  PluginOptions,
  SupportedBiometricType,
  VaultErrorCodes,
  VaultError,
  VaultConfig,
} from "@ionic-enterprise/identity-vault";

import { Plugins } from "@capacitor/core";
import { MQ } from "../mq";
import MockDelay from "../../utils/helpers/MockDelay";

const { Storage, Device } = Plugins;

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

let mqm: any;

interface AuthenticatorSession extends DefaultSession {
  // username & token are inherited
  identity: string;
}

class IdentityService extends IonicIdentityVaultUser<AuthenticatorSession> {
  // private deviceId: any;
  private email: string | undefined;

  constructor() {
    super(
      { ready: () => Promise.resolve() },
      {
        androidPromptTitle: "Abaxx Vault",
        androidPromptSubtitle: "ID++ Protocol",
        androidPromptDescription: "unlock me",
        restoreSessionOnReady: true,
        unlockOnReady: false,
        unlockOnAccess: false,
        // lockAfter: 15000,
        hideScreenOnBackground: true,
        allowSystemPinFallback: true,
        shouldClearVaultAfterTooManyFailedAttempts: false,
      }
    );
    // Device.getInfo().then((d) => {
    //   this.deviceId = d.uuid;
    // });
  }

  async initAppConfig(): Promise<any> {
    const config: any = await window.idDwn.dwn.metadata.get(
      process.env.REACT_APP_CONFIG
    );
    const { DEV_PN_PUB_KEY, DEV_PN_SUB_KEY, PRD_PN_PUB_KEY, PRD_PN_SUB_KEY } =
      JSON.parse(config);

    let publishKey = "";
    let subscribeKey = "";

    if (process.env.REACT_APP_ENVIRONMENT === "local") {
      publishKey = DEV_PN_PUB_KEY;
      subscribeKey = DEV_PN_SUB_KEY;
    } else {
      publishKey = PRD_PN_PUB_KEY;
      subscribeKey = PRD_PN_SUB_KEY;
    }

    if (publishKey && subscribeKey) {
      mqm = new MQ({
        publishKey,
        subscribeKey,
      });
    }

    return mqm;
  }

  async login(session: AuthenticatorSession, authMode: number): Promise<void> {
    this.email = session.username;
    // const mode = authMode ? authMode : await identitySettings.getAuthMode();
    await super.login(session, authMode);
  }

  logout(): Promise<void> {
    this.email = undefined;
    return super.logout();
  }

  async getEmail(): Promise<string | undefined> {
    if (!this.email) {
      await this.restoreSession();
    }
    return this.email || "";
  }

  async getToken(): Promise<string> {
    if (!this.token) {
      await this.restoreSession();
    }
    return this.token || "";
  }

  async restoreSession(): Promise<AuthenticatorSession | undefined> {
    try {
      return await super.restoreSession();
    } catch (e) {
      // @ts-ignore
      if (e.message && mqm) {
        const device = await Device.getInfo();
        // Device.getInfo().then((d) => {
        //   this.deviceId = d.uuid;
        // });

        await mqm.pubnub.publish({
          // channel: this.deviceId,
          channel: device.uuid,
          meta: {
            type: "setNotification",
          },
          message: {
            isError: true,
            // @ts-ignore
            message: e.message,
          },
        });
      }

      // @ts-ignore
      if (e.code === VaultErrorCodes.VaultLocked) {
        const vault = await this.getVault();
        await vault.clear();
      }
    }
  }

  onSessionRestored(session: DefaultSession) {
    this.email = session.username;
  }

  onSetupError(error: VaultError): void {
    console.error("Get error during setup", error);
  }

  onConfigChange(config: VaultConfig): void {
    // console.log("Got a config update: ", config);
  }

  async onVaultReady(config: VaultConfig): Promise<void> {
    console.log("Vault ready: ", config);
    const device = await Device.getInfo();

    if (device.uuid) {
      await MockDelay(5000);
      mqm = await this.initAppConfig();
      if (mqm) {
        mqm.pubnub.publish({
          channel: device.uuid,
          meta: {
            type: "updateVaultState",
          },
          message: {},
        });
      }
    }
  }

  onVaultUnlocked(config: VaultConfig): void {
    console.log("Unlocked with config: ", config);
  }

  async onPasscodeRequest(isPasscodeSetRequest: boolean): Promise<string> {
    if (!isPasscodeSetRequest) {
      await Storage.set({ key: "isPincodeOpen", value: "true" });
      const device = await Device.getInfo();
      if (mqm && device.uuid) {
        await mqm.pubnub.publish({
          channel: device.uuid,
          meta: {
            type: "showPincode",
          },
          message: {
            isSetPincode: isPasscodeSetRequest,
          },
        });
      }
    }

    const pin = await this.waitForPasscode();

    if (!pin) {
      // eslint-disable-next-line no-throw-literal
      throw {
        code: VaultErrorCodes.UserCanceledInteraction,
        message: "User has canceled supplying the application passcode",
      };
    }
    return pin;
  }

  private waitForPasscode(): Promise<string> {
    return new Promise((resolve) => {
      const pinCodeChecker = async () => {
        const isPincodeOpen: any = await Storage.get({ key: "isPincodeOpen" });
        if (isPincodeOpen && isPincodeOpen.value === "false") {
          const enteredPincode: any = await Storage.get({
            key: "enteredPincode",
          });
          const pin =
            enteredPincode && enteredPincode.value ? enteredPincode.value : "";
          resolve(pin);
        } else {
          setTimeout(() => {
            pinCodeChecker();
          }, 1000);
        }
      };

      setTimeout(() => {
        pinCodeChecker();
      }, 1000);
    });
  }

  async onVaultLocked() {
    // const device = await Device.getInfo();
    let isLockFromSwitchAccount: boolean = false;

    const response: any = await Storage.get({
      key: "isLockFromSwitchAccount",
    });

    if (response && response.value === "true") {
      isLockFromSwitchAccount = true;
    }

    const device = await Device.getInfo();

    if (mqm && device.uuid) {
      await mqm.pubnub.publish({
        channel: device.uuid,
        meta: {
          type: "logout",
        },
        message: {
          appId: process.env.REACT_APP_APP_ID,
          isLockFromSwitchAccount,
        },
      });
    }
  }

  getPlugin(): IonicNativeAuthPlugin {
    if (isPlatform("cordova")) {
      return super.getPlugin();
    }
    return browserAuthPlugin;
  }
}
const identityService = new IdentityService();

class AuthenticationService {
  async login(
    username: string,
    password: string
  ): Promise<{ success: boolean; token?: string; user?: User }> {
    const response = await fetch(`${process.env.API_URL}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });
    return await response.json();
  }

  async logout(): Promise<void> {
    const token = await identityService.getToken();
    if (token) {
      await fetch(`${process.env.API_URL}/logout`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
      });
    }
  }
}
const authentication = new AuthenticationService();

class BrowserAuthPlugin implements IonicNativeAuthPlugin {
  getVault(config: PluginOptions): IdentityVault {
    config.onReady!(browserAuthService);
    return browserAuthService;
  }
}
const browserAuthPlugin: IonicNativeAuthPlugin = new BrowserAuthPlugin();

class BrowserAuthService implements IdentityVault {
  config = {
    authMode: AuthMode.SecureStorage,
    descriptor: {
      username: "",
      vaultId: "",
    },
    isBiometricsEnabled: false,
    isPasscodeEnabled: false,
    isPasscodeSetupNeeded: false,
    isSecureStorageModeEnabled: true,
    hideScreenOnBackground: false,
    lockAfter: 50000,
  };

  unsubscribe(): Promise<void> {
    return Promise.resolve();
  }

  clear(): Promise<void> {
    return Storage.clear();
  }

  lock(): Promise<void> {
    return Promise.resolve();
  }

  isLocked(): Promise<boolean> {
    return Promise.resolve(false);
  }

  async isInUse(): Promise<boolean> {
    const { value } = await Storage.get({ key: "session" });
    return !!value;
  }

  getAvailableHardware(): Promise<SupportedBiometricType[]> {
    return Promise.resolve([]);
  }

  getConfig(): Promise<PluginConfiguration> {
    return Promise.resolve(this.config);
  }

  remainingAttempts(): Promise<number> {
    return Promise.resolve(5);
  }

  getUsername(): Promise<string> {
    return Promise.resolve("MyUsername");
  }

  storeToken(token: any): Promise<void> {
    return Promise.resolve();
  }

  getToken(): Promise<any> {
    return Promise.resolve("MyToken");
  }

  async storeValue(key: string, value: any): Promise<void> {
    await Storage.set({ key, value: JSON.stringify(value) });
  }

  async getValue(key: string): Promise<any> {
    const { value } = await Storage.get({ key });
    return value && JSON.parse(value);
  }

  async removeValue(key: string): Promise<void> {
    await Storage.remove({ key });
  }

  async getKeys(): Promise<Array<string>> {
    const { keys } = await Storage.keys();
    return keys;
  }

  getBiometricType(): Promise<BiometricType> {
    const none: BiometricType = "none";
    return Promise.resolve(none);
  }

  setBiometricsEnabled(isBiometricsEnabled: boolean): Promise<void> {
    return Promise.resolve();
  }

  isBiometricsEnabled(): Promise<boolean> {
    return Promise.resolve(false);
  }

  isBiometricsAvailable(): Promise<boolean> {
    return Promise.resolve(false);
  }

  isBiometricsSupported(): Promise<boolean> {
    return Promise.resolve(false);
  }

  async isLockedOutOfBiometrics(): Promise<boolean> {
    return false;
  }

  isPasscodeSetupNeeded(): Promise<boolean> {
    return Promise.resolve(false);
  }

  setPasscode(passcode?: string): Promise<void> {
    return Promise.resolve();
  }

  isPasscodeEnabled(): Promise<boolean> {
    return Promise.resolve(false);
  }

  isSecureStorageModeEnabled(): Promise<boolean> {
    return Promise.resolve(true);
  }

  setPasscodeEnabled(isPasscodeEnabled: boolean): Promise<void> {
    return Promise.resolve();
  }

  setSecureStorageModeEnabled(enabled: boolean): Promise<void> {
    return Promise.resolve();
  }

  unlock(usingPasscode?: boolean, passcode?: string): Promise<void> {
    return Promise.resolve();
  }
}
const browserAuthService = new BrowserAuthService();

class SettingsService {
  setAuthMode(value: AuthMode): Promise<void> {
    return Storage.set({ key: "authMode", value: value.toString() });
  }

  async getAuthMode(): Promise<AuthMode | undefined> {
    const { value } = await Storage.get({ key: "authMode" });
    return value ? parseInt(value) : undefined;
  }
}
const identitySettings = new SettingsService();

export {
  authentication,
  browserAuthPlugin,
  browserAuthService,
  identityService,
  identitySettings,
};
